'use client'
import { markBillingAsPaidChecked } from '@/lib/billing/mark-billing-paid'

import ConfirmModal from '@/components/confirm-modal'
import DateRangeFilter from '@/components/date-range-filter'
import { ClientSelector } from '@/components/client/ClientSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { useEntityAccess } from '@/lib/hooks/use-entity-access'
import { UserSelector } from '@/components/UserSelector'
import {
    Billing,
    BillingStatus,
} from '@/lib/api/common/interfaces/billing.interface'
import {
    BillingSummary,
    cancelBilling,
    deleteBilling,
    getBillings,
    getBillingsSummary,
} from '@/lib/api/financial/financial-billing-api'
import { billingCategoryLabel } from '@/lib/billing/billing-categories'
import { isEditableBillingStatus } from '@/lib/billing/billing-editable-status'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    MdAdd,
    MdArrowForward,
    MdAttachMoney,
    MdCancel,
    MdCheckCircle,
    MdClose,
    MdDelete,
    MdEdit,
    MdReceipt,
    MdSearch,
    MdVisibility,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const STATUS_LABELS: Record<BillingStatus, string> = {
    PENDING: 'Pendente',
    PAID: 'Pago',
    OVERDUE: 'Vencido',
    CANCELLED: 'Cancelado',
    REFUNDED: 'Estornado',
    PARTIALLY_REFUNDED: 'Parcialmente Estornado',
    PARTIALLY_PAID: 'Parcial',
    IN_DISPUTE: 'Em disputa',
    WRITTEN_OFF: 'Baixado',
}

/**
 * Métodos liquidados pelo gateway (Asaas): recebem baixa automática por webhook,
 * então não oferecemos baixa manual — o backend recusa e a fila de aprovação não
 * deve ser poluída com eles.
 */
const GATEWAY_METHODS = ['PIX', 'BOLETO', 'CREDIT_CARD']

const STATUS_BADGE_CLASS: Record<BillingStatus, string> = {
    PENDING: 'badge-warning',
    PAID: 'badge-success text-white',
    OVERDUE: 'badge-error text-white',
    CANCELLED: 'badge-ghost',
    REFUNDED: 'badge-info text-white',
    PARTIALLY_REFUNDED: 'badge-info',
    PARTIALLY_PAID: 'badge-warning',
    IN_DISPUTE: 'badge-error text-white',
    WRITTEN_OFF: 'badge-ghost',
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
    PIX: 'PIX',
    BOLETO: 'Boleto',
    CREDIT_CARD: 'Cartão de Crédito',
    DEBIT_CARD: 'Cartão de Débito',
    CASH: 'Dinheiro',
    BANK_TRANSFER: 'Transferência',
    CHECK: 'Cheque',
    TERMINAL: 'Maquininha',
    OTHER: 'Outro',
}

const STATUS_FILTERS: { value: string; label: string }[] = [
    { value: '', label: 'Todos status' },
    { value: 'PENDING', label: 'Pendentes' },
    { value: 'PAID', label: 'Pagos' },
    { value: 'OVERDUE', label: 'Vencidos' },
    { value: 'CANCELLED', label: 'Cancelados' },
    { value: 'REFUNDED', label: 'Reembolsados' },
    { value: 'PARTIALLY_PAID', label: 'Parciais' },
    { value: 'IN_DISPUTE', label: 'Em disputa' },
    { value: 'WRITTEN_OFF', label: 'Baixados' },
]

const TYPE_FILTERS: { value: string; label: string }[] = [
    { value: '', label: 'Todos' },
    { value: 'RECEIVABLE', label: 'Receber' },
    { value: 'PAYABLE', label: 'Pagar' },
]

/**
 * Cobrança tem três datas com significados distintos e o operador precisa escolher
 * qual o período filtra: "o que vence este mês" e "o que recebi este mês" são
 * perguntas diferentes. Os valores espelham a whitelist do backend
 * (`BILLING_DATE_FILTER_FIELDS`).
 */
type BillingDateField = 'dueDate' | 'paidAt' | 'createdAt'

const DATE_FIELD_FILTERS: { value: BillingDateField; label: string }[] = [
    { value: 'createdAt', label: 'Criação' },
    { value: 'dueDate', label: 'Vencimento' },
    { value: 'paidAt', label: 'Pagamento' },
]

const PAYMENT_METHOD_FILTERS: { value: string; label: string }[] = [
    { value: '', label: 'Todas as formas' },
    ...Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({
        value,
        label,
    })),
]

function formatCurrency(cents: number): string {
    return (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })
}

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('pt-BR')
}

export default function BillingsListPage() {
    const { user, isPartner } = useAuth()
    const { canSeeProfessionals } = useEntityAccess()
    const serviceLabels = useServiceLabel()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'financial',
        'billings'
    )
    const [searchInput, setSearchInput] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [typeFilter, setTypeFilter] = useState('')
    const [paymentMethodFilter, setPaymentMethodFilter] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [billingToDelete, setBillingToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [showPayModal, setShowPayModal] = useState(false)
    const [billingToPay, setBillingToPay] = useState<Billing | null>(null)
    const [paying, setPaying] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [billingToCancel, setBillingToCancel] = useState<number | null>(null)
    const [cancelling, setCancelling] = useState(false)
    const [professionalFilter, setProfessionalFilter] = useState<number | null>(
        null
    )
    const [clientFilter, setClientFilter] = useState<number | null>(null)
    const [partnerFilter, setPartnerFilter] = useState<number | null>(null)
    /** Quem lançou a cobrança (nos atendimentos este mesmo campo é o vendedor). */
    const [createdByFilter, setCreatedByFilter] = useState<number | null>(null)
    const [createdByName, setCreatedByName] = useState<string | null>(null)
    const [dateRange, setDateRange] = useState<{
        startDate?: string
        endDate?: string
    }>({})
    /**
     * Criação como padrão: o operador abre a lista para achar o que acabou de
     * lançar, não o que vence hoje — mesma escolha já feita nos relatórios
     * (`DEFAULT_DATE_BASIS` em reports.service).
     */
    const [dateField, setDateField] = useState<BillingDateField>('createdAt')

    const debouncedSearch = useDebounce(searchInput, 300)
    const [urlPage, setUrlPage] = useUrlPage()

    const isHealthSegment = user?.company?.segment === 'HEALTH'

    const filters = useMemo(
        () => ({
            search: debouncedSearch || undefined,
            companyId: user?.companyId,
            status: statusFilter || undefined,
            type: typeFilter || undefined,
            paymentMethod: paymentMethodFilter || undefined,
            clientId: clientFilter || undefined,
            partnerId: partnerFilter || undefined,
            professionalId: professionalFilter || undefined,
            createdById: createdByFilter || undefined,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            // Sem janela de datas o campo é irrelevante — não enviar evita variar a
            // chave de cache da listagem por um filtro que não está em uso.
            dateField: dateRange.startDate ? dateField : undefined,
        }),
        [
            debouncedSearch,
            user?.companyId,
            statusFilter,
            typeFilter,
            paymentMethodFilter,
            clientFilter,
            partnerFilter,
            professionalFilter,
            createdByFilter,
            dateRange,
            dateField,
        ]
    )

    const fetchFn = useCallback((params: any) => {
        return getBillings(params)
    }, [])

    const { items, loading, page, limit, total, setPage, reload } =
        usePaginatedPage<Billing, typeof filters>({
            fetchFn,
            filters,
            limit: 15,
            enabled: !!user?.companyId,
            page: urlPage,
            onPageChange: setUrlPage,
        })

    // Totais do vendedor: só carregam com o filtro de "Criado por" ativo — é a
    // pergunta que justifica o número ("quanto o fulano lançou / quanto entrou").
    // Sem filtro seria a soma da empresa inteira, que a tela não promete.
    const [summary, setSummary] = useState<BillingSummary | null>(null)
    const [summaryLoading, setSummaryLoading] = useState(false)

    useEffect(() => {
        if (!createdByFilter || !user?.companyId) {
            setSummary(null)
            return
        }
        let cancelled = false
        setSummaryLoading(true)
        // Mesmos filtros da lista: o total precisa corresponder às linhas exibidas.
        getBillingsSummary(filters)
            .then(res => {
                if (cancelled) return
                setSummary(res.success && res.result ? res.result : null)
            })
            .catch(() => {
                if (!cancelled) setSummary(null)
            })
            .finally(() => {
                if (!cancelled) setSummaryLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [createdByFilter, filters, user?.companyId])

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
    }

    const handleMarkAsPaid = async () => {
        if (!billingToPay) return

        setPaying(true)
        try {
            const response = await markBillingAsPaidChecked(billingToPay.id)
            if (response.success) {
                const result = response.result as any
                if (result?.pending) {
                    toast.info(
                        result.message ||
                            'Solicitação enviada para aprovação de um operador financeiro'
                    )
                } else {
                    toast.success('Cobrança marcada como paga')
                }
                setShowPayModal(false)
                setBillingToPay(null)
                reload()
            } else {
                toast.error(response.error || 'Erro ao marcar como paga')
            }
        } catch (error) {
            toast.error('Erro ao marcar como paga')
        } finally {
            setPaying(false)
        }
    }

    const handleCancel = async () => {
        if (!billingToCancel) return

        setCancelling(true)
        try {
            const response = await cancelBilling(billingToCancel)
            if (response.success) {
                const result = response.result as any
                if (result?.pending) {
                    toast.info(
                        result.message ||
                            'Solicitação de cancelamento enviada para aprovação'
                    )
                } else {
                    toast.success('Cobrança cancelada com sucesso')
                }
                setShowCancelModal(false)
                setBillingToCancel(null)
                reload()
            } else {
                toast.error(response.error || 'Erro ao cancelar cobrança')
            }
        } catch (error) {
            toast.error('Erro ao cancelar cobrança')
        } finally {
            setCancelling(false)
        }
    }

    const handleDelete = async () => {
        if (!billingToDelete) return

        setDeleting(true)
        try {
            const response = await deleteBilling(billingToDelete)
            if (response.success) {
                toast.success('Cobrança excluída com sucesso')
                setBillingToDelete(null)
                setShowDeleteModal(false)
                reload()
            } else {
                toast.error(response.error || 'Erro ao excluir cobrança')
            }
        } catch (error) {
            toast.error('Erro ao excluir cobrança')
        } finally {
            setDeleting(false)
        }
    }

    /** Distingue "nada cadastrado" de "nada casou com o filtro" no estado vazio. */
    const hasActiveFilters = !!(
        searchInput ||
        statusFilter ||
        typeFilter ||
        paymentMethodFilter ||
        professionalFilter ||
        clientFilter ||
        partnerFilter ||
        dateRange.startDate
    )

    const openDeleteModal = (billingId: number) => {
        setBillingToDelete(billingId)
        setShowDeleteModal(true)
    }

    if (loading && items.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 animate-fade-in">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando cobranças...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 animate-fade-in">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg">
                        <MdAttachMoney className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {typeFilter === 'PAYABLE'
                                ? 'Contas a Pagar'
                                : typeFilter === 'RECEIVABLE'
                                  ? 'Contas a Receber'
                                  : 'Financeiro'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total} {total === 1 ? 'lançamento' : 'lançamentos'}{' '}
                            cadastrados
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() => navigate('/financial/billings/new')}
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">
                            Novo Lançamento
                        </span>
                    </button>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                <form
                    onSubmit={handleSearch}
                    className="flex items-center gap-2"
                >
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por cliente, descrição, código de referência..."
                            className="input input-bordered w-full pl-10"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                        />
                    </div>
                    {searchInput && (
                        <button
                            type="button"
                            onClick={() => setSearchInput('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </form>

                {/* Uma barra só: "Filtrar por"/período moram aqui dentro em vez de numa
                    segunda linha fixa — assim eles preenchem a sobra da linha dos demais
                    filtros e só quebram quando não couberem, junto com o resto. */}
                <div className="flex flex-wrap items-end gap-2">
                    {TYPE_FILTERS.map(f => (
                        <button
                            className={
                                'btn btn-sm' +
                                (typeFilter === f.value
                                    ? ' btn-secondary'
                                    : ' btn-ghost')
                            }
                            onClick={e => setTypeFilter(f.value)}
                        >
                            {f.label}
                        </button>
                    ))}

                    <select
                        className="select select-bordered select-sm max-w-37.5!"
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                    >
                        {STATUS_FILTERS.map(f => (
                            <option key={f.value} value={f.value}>
                                {f.label}
                            </option>
                        ))}
                    </select>

                    <select
                        className="select select-bordered select-sm max-w-37.5!"
                        value={paymentMethodFilter}
                        onChange={e => setPaymentMethodFilter(e.target.value)}
                        title="Forma de pagamento"
                    >
                        {PAYMENT_METHOD_FILTERS.map(f => (
                            <option key={f.value} value={f.value}>
                                {f.label}
                            </option>
                        ))}
                    </select>

                    {isHealthSegment && canSeeProfessionals && (
                        <div className="w-56">
                            <ProfessionalSearchSelector
                                value={professionalFilter}
                                onChange={id => setProfessionalFilter(id)}
                                label=""
                                placeholder="Profissional"
                                size="sm"
                            />
                        </div>
                    )}

                    <div className="w-56">
                        <ClientSelector
                            value={clientFilter}
                            onChange={id => setClientFilter(id)}
                            label=""
                            placeholder={
                                isHealthSegment ? 'Paciente' : 'Cliente'
                            }
                            size="sm"
                        />
                    </div>

                    {!isPartner && (
                        <PartnerSelector
                            value={partnerFilter}
                            onChange={id => setPartnerFilter(id)}
                            label=""
                            placeholder="Parceiro"
                            className="!w-48"
                            size="sm"
                        />
                    )}

                    {!isPartner && (
                        <UserSelector
                            value={createdByFilter}
                            onChange={(id, u) => {
                                setCreatedByFilter(id)
                                setCreatedByName(u?.name ?? null)
                            }}
                            label=""
                            placeholder="Criado por"
                            className="!w-48"
                            size="sm"
                        />
                    )}

                    <div className="form-control">
                        <label className="label py-0">
                            <span className="label-text text-xs font-medium">
                                Filtrar por
                            </span>
                        </label>
                        <select
                            className="select select-bordered select-sm w-36"
                            value={dateField}
                            onChange={e =>
                                setDateField(e.target.value as BillingDateField)
                            }
                        >
                            {DATE_FIELD_FILTERS.map(f => (
                                <option key={f.value} value={f.value}>
                                    {f.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <DateRangeFilter onChange={setDateRange} className="gap-2" />
                </div>
            </div>

            {createdByFilter && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-wrap items-center gap-x-8 gap-y-3">
                    <div className="flex items-center gap-2 text-sm text-base-content/70">
                        <MdAttachMoney className="text-lg text-emerald-600" />
                        Totais de{' '}
                        <strong className="text-base-content">
                            {createdByName || 'vendedor selecionado'}
                        </strong>
                    </div>

                    {summaryLoading && !summary ? (
                        <span className="loading loading-spinner loading-sm" />
                    ) : summary ? (
                        <>
                            <div>
                                <p className="text-xs text-base-content/60">
                                    A receber ({summary.receivable.count})
                                </p>
                                <p className="text-lg font-bold text-emerald-600">
                                    {formatCurrency(summary.receivable.amount)}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-base-content/60">
                                    Recebido
                                </p>
                                <p className="text-lg font-bold text-base-content">
                                    {formatCurrency(
                                        summary.receivable.paidAmount
                                    )}
                                </p>
                            </div>
                            {/* Conta a pagar (repasse a parceiro, por exemplo) só aparece
                                quando existe no recorte — somá-la ao "a receber" misturaria
                                receita com despesa. */}
                            {summary.payable.count > 0 && (
                                <div>
                                    <p className="text-xs text-base-content/60">
                                        A pagar ({summary.payable.count})
                                    </p>
                                    <p className="text-lg font-bold text-red-600">
                                        {formatCurrency(summary.payable.amount)}
                                    </p>
                                </div>
                            )}
                        </>
                    ) : (
                        <span className="text-sm text-base-content/60">
                            Não foi possível calcular os totais
                        </span>
                    )}
                </div>
            )}

            {items.length === 0 && !loading ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdReceipt className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {hasActiveFilters
                                ? 'Nenhum lançamento encontrado'
                                : 'Nenhum lançamento cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {hasActiveFilters
                                ? 'Tente ajustar sua busca ou filtro'
                                : 'Comece adicionando lançamentos para que eles apareçam aqui.'}
                        </p>
                        {!hasActiveFilters && canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/financial/billings/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Criar primeira cobrança
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {items.map(billing => (
                        <div
                            key={billing.id}
                            className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                            onClick={() =>
                                navigate(`/financial/billings/${billing.id}`)
                            }
                        >
                            <div className="flex items-start gap-4 p-4">
                                <div
                                    className={`relative w-16 h-16 flex-shrink-0 bg-gradient-to-br ${
                                        billing.type === 'PAYABLE'
                                            ? 'from-red-500/20 to-red-500/10 border-red-500/20'
                                            : 'from-emerald-500/20 to-emerald-500/10 border-emerald-500/20'
                                    } rounded-xl flex items-center justify-center border`}
                                >
                                    <MdReceipt
                                        className={`text-3xl ${
                                            billing.type === 'PAYABLE'
                                                ? 'text-red-600'
                                                : 'text-emerald-600'
                                        }`}
                                    />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <div>
                                            <h3 className="text-base font-bold text-base-content line-clamp-1 group-hover:text-secondary transition-colors">
                                                {billing.type === 'PAYABLE'
                                                    ? billing.supplier?.name ||
                                                      billing.supplierName ||
                                                      billing.partner
                                                          ?.tradingName ||
                                                      billing.partner?.name ||
                                                      billing.professional
                                                          ?.name ||
                                                      billing.hospital
                                                          ?.tradingName ||
                                                      billing.hospital?.name ||
                                                      'Lançamento'
                                                    : billing.client?.name ||
                                                      billing.partner?.name ||
                                                      billing.partner
                                                          ?.tradingName ||
                                                      'Lançamento'}
                                            </h3>
                                            {billing.description && (
                                                <p className="text-sm text-base-content/60 line-clamp-1">
                                                    {billing.description}
                                                </p>
                                            )}
                                        </div>
                                        <div
                                            className="flex gap-1 flex-shrink-0"
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <button
                                                onClick={() =>
                                                    navigate(
                                                        `/financial/billings/${billing.id}`
                                                    )
                                                }
                                                className="btn btn-circle btn-sm btn-ghost"
                                                title="Ver detalhes"
                                            >
                                                <MdVisibility className="text-lg" />
                                            </button>
                                            {canUpdate &&
                                                (billing.status ===
                                                    'PENDING' ||
                                                    billing.status ===
                                                        'OVERDUE') && (
                                                    <>
                                                        {/* Cobrança de gateway
                                                            (PIX/boleto/cartão) recebe
                                                            baixa automática do Asaas —
                                                            baixa manual daqui não é
                                                            oferecida (o backend recusa).
                                                            Ver detalhes para verificar
                                                            o pagamento no gateway. */}
                                                        {!GATEWAY_METHODS.includes(
                                                            billing.paymentMethod ||
                                                                ''
                                                        ) && (
                                                        <button
                                                            onClick={() => {
                                                                setBillingToPay(
                                                                    billing
                                                                )
                                                                setShowPayModal(
                                                                    true
                                                                )
                                                            }}
                                                            className="btn btn-circle btn-sm btn-ghost text-success"
                                                            title="Marcar como pago"
                                                        >
                                                            <MdCheckCircle className="text-lg" />
                                                        </button>
                                                        )}
                                                        <button
                                                            onClick={() => {
                                                                setBillingToCancel(
                                                                    billing.id
                                                                )
                                                                setShowCancelModal(
                                                                    true
                                                                )
                                                            }}
                                                            className="btn btn-circle btn-sm btn-ghost text-error"
                                                            title="Cancelar"
                                                        >
                                                            <MdCancel className="text-lg" />
                                                        </button>
                                                    </>
                                                )}
                                            {canUpdate &&
                                                isEditableBillingStatus(
                                                    billing.status
                                                ) && (
                                                    <button
                                                        onClick={() =>
                                                            navigate(
                                                                `/financial/billings/${billing.id}/edit`
                                                            )
                                                        }
                                                        className="btn btn-circle btn-sm btn-ghost"
                                                        title="Editar"
                                                    >
                                                        <MdEdit className="text-lg" />
                                                    </button>
                                                )}
                                            {canDelete &&
                                                ![
                                                    'CONSULTATION',
                                                    'EXAM',
                                                    'PROCEDURE',
                                                    'TELECONSULTATION',
                                                ].includes(
                                                    (billing as any).metadata
                                                        ?.sourceType
                                                ) && (
                                                    <button
                                                        onClick={() =>
                                                            openDeleteModal(
                                                                billing.id
                                                            )
                                                        }
                                                        className="btn btn-circle btn-sm btn-ghost text-error"
                                                        title="Excluir"
                                                    >
                                                        <MdDelete className="text-lg" />
                                                    </button>
                                                )}
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span
                                                className={`badge badge-sm ${
                                                    billing.type === 'PAYABLE'
                                                        ? 'badge-error'
                                                        : 'badge-success'
                                                }`}
                                            >
                                                {billing.type === 'PAYABLE'
                                                    ? 'Conta a Pagar'
                                                    : 'Cobrança'}
                                            </span>
                                            <span
                                                className={`badge ${STATUS_BADGE_CLASS[billing.status]}`}
                                            >
                                                {STATUS_LABELS[billing.status]}
                                            </span>
                                            {billing.isReconciled && (
                                                <span className="badge badge-success badge-outline badge-sm">
                                                    Conciliado
                                                </span>
                                            )}
                                            {billing.reconciliationPending && (
                                                <span className="badge badge-warning badge-outline badge-sm">
                                                    Em Análise
                                                </span>
                                            )}
                                            {/* Coberta por convênio: o `amount` é zero de
                                                propósito e listar "R$ 0,00" faria a linha
                                                parecer um lançamento sem valor. */}
                                            <span className="text-sm font-semibold text-base-content">
                                                {formatCurrency(
                                                    billing.invoicedAmount ??
                                                        billing.amount
                                                )}
                                            </span>
                                            {billing.payerType ===
                                                'HEALTH_PLAN' && (
                                                <span className="badge badge-secondary badge-outline badge-sm">
                                                    {billing.healthPlan?.name ||
                                                        'Convênio'}
                                                </span>
                                            )}
                                            <span className="text-xs text-base-content/50">
                                                Venc.:{' '}
                                                {formatDate(billing.dueDate)}
                                            </span>
                                            {billing.paymentMethod && (
                                                <span className="badge badge-outline badge-sm">
                                                    {PAYMENT_METHOD_LABELS[
                                                        billing.paymentMethod
                                                    ] || billing.paymentMethod}
                                                </span>
                                            )}
                                            {billing.type === 'PAYABLE' &&
                                                billing.category && (
                                                    <span className="badge badge-ghost badge-sm">
                                                        {billingCategoryLabel(
                                                            billing.category,
                                                            serviceLabels
                                                        )}
                                                    </span>
                                                )}
                                            {billing.partner && (
                                                <span className="badge badge-ghost badge-sm">
                                                    {billing.partner
                                                        .tradingName ||
                                                        billing.partner.name}
                                                </span>
                                            )}
                                            {billing.referenceCode && (
                                                <span className="badge badge-ghost badge-sm">
                                                    Ref: {billing.referenceCode}
                                                </span>
                                            )}
                                        </div>

                                        <MdArrowForward className="text-xl text-base-content/30 group-hover:text-secondary group-hover:translate-x-1 transition-all" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="fatura"
                        />
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setBillingToDelete(null)
                }}
                onConfirm={handleDelete}
                title="Excluir cobrança"
                message="Tem certeza de que deseja excluir esta cobrança? Esta ação não pode ser desfeita."
                loading={deleting}
                confirmButtonText="Excluir"
            />

            <ConfirmModal
                isOpen={showPayModal}
                onClose={() => {
                    setShowPayModal(false)
                    setBillingToPay(null)
                }}
                onConfirm={handleMarkAsPaid}
                title="Marcar como pago"
                message={
                    billingToPay
                        ? `Deseja marcar a cobrança de ${formatCurrency(billingToPay.amount)} para ${billingToPay.client?.name || billingToPay.partner?.name || 'o destinatário'} como paga?`
                        : ''
                }
                loading={paying}
                confirmButtonText="Confirmar pagamento"
            />

            <ConfirmModal
                isOpen={showCancelModal}
                onClose={() => {
                    setShowCancelModal(false)
                    setBillingToCancel(null)
                }}
                onConfirm={handleCancel}
                title="Cancelar cobrança"
                message="Tem certeza de que deseja cancelar esta cobrança?"
                loading={cancelling}
                confirmButtonText="Cancelar cobrança"
            />
        </div>
    )
}
